"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.wordpressWebPublisher = exports.WORDPRESS_WEB_PUBLISHER_BG = exports.WORDPRESS_WEB_PUBLISHER_COLOR = exports.WORDPRESS_WEB_PUBLISHER_CATEGORY = exports.wordpressWebPublisherArgsSchema = exports.WordPressWebPublisherAction = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
var webPublisher_1 = require("./webPublisher");
// Import WordPress-specific schemas from wordpressActions (excluding performTask)
// We'll reuse the existing schemas but remove performTask action
// WordPress-specific extended actions (9 actions, excluding performTask)
var wordpressExtendedActionSchema = zod_1.z.enum([
    'getContent',
    'listContent',
    'publishContent',
    'updateContent',
    'uploadMedia',
    'getMedia',
    'updateMedia',
    'listCategories',
    'listTags',
]);
// All WordPress WebPublisher actions (3 core + 9 extended = 12 total)
exports.WordPressWebPublisherAction = zod_1.z.enum([
    // Core WebPublisher actions
    'create',
    'update',
    'subagentTaskRunner',
    // Extended WordPress actions
    'getContent',
    'listContent',
    'publishContent',
    'updateContent',
    'uploadMedia',
    'getMedia',
    'updateMedia',
    'listCategories',
    'listTags',
]);
// Import WordPress action args schemas from wordpressActions
// We'll need to create individual schemas for each extended action
var wordpressGetContentArgsSchema = zod_1.z.discriminatedUnion('contentType', [
    zod_1.z.object({
        contentType: zod_1.z.literal('page'),
        contentUrl: zod_1.z.string().describe('The URL or ID of the WordPress page to fetch'),
    }),
    zod_1.z.object({
        contentType: zod_1.z.literal('post'),
        contentUrl: zod_1.z.string().describe('The URL or ID of the WordPress post to fetch'),
    }),
]);
var wordpressListContentArgsSchema = zod_1.z.discriminatedUnion('contentType', [
    zod_1.z.object({
        contentType: zod_1.z.literal('page'),
        status: zod_1.z
            .enum(['publish', 'draft', 'pending', 'private', 'any'])
            .nullish()
            .describe('Filter by status'),
        search: zod_1.z.string().nullish().describe('Search query to filter content'),
        perPage: zod_1.z.number().min(1).max(100).default(10).describe('Number of items per page (1-100)'),
        page: zod_1.z.number().min(1).default(1).describe('Page number for pagination'),
        order: zod_1.z.enum(['asc', 'desc']).default('desc').describe('Sort order'),
        parent: zod_1.z.number().nullish().describe('Filter by parent page ID'),
        orderBy: zod_1.z
            .enum(['date', 'modified', 'title', 'menu_order'])
            .default('date')
            .describe('Sort pages by this field'),
    }),
    zod_1.z.object({
        contentType: zod_1.z.literal('post'),
        status: zod_1.z
            .enum(['publish', 'draft', 'pending', 'private', 'any'])
            .nullish()
            .describe('Filter by status'),
        search: zod_1.z.string().nullish().describe('Search query to filter content'),
        perPage: zod_1.z.number().min(1).max(100).default(10).describe('Number of items per page (1-100)'),
        page: zod_1.z.number().min(1).default(1).describe('Page number for pagination'),
        order: zod_1.z.enum(['asc', 'desc']).default('desc').describe('Sort order'),
        categories: zod_1.z.array(zod_1.z.number()).nullish().describe('Filter by category IDs'),
        tags: zod_1.z.array(zod_1.z.number()).nullish().describe('Filter by tag IDs'),
        author: zod_1.z.number().nullish().describe('Filter by author user ID'),
        orderBy: zod_1.z
            .enum(['date', 'modified', 'title', 'relevance'])
            .default('date')
            .describe('Sort posts by this field'),
    }),
]);
// Content input schemas
var markdownContentSchema = zod_1.z.object({
    contentType: zod_1.z.literal('markdown'),
    content: zod_1.z
        .string()
        .describe('The page content (Markdown format) - will be converted to WordPress blocks'),
});
var blocksContentSchema = zod_1.z.object({
    contentType: zod_1.z.literal('blocks'),
    content: zod_1.z
        .string()
        .describe('Raw WordPress Gutenberg blocks obtained from getContent action - DO NOT generate blocks manually, always fetch existing blocks first and modify them'),
});
var sectionsContentSchema = zod_1.z.object({
    contentType: zod_1.z.literal('sections'),
    sections: zod_1.z
        .array(zod_1.z.any())
        .min(1)
        .describe('Structured content sections that generate proper WordPress Gutenberg blocks (compatible with seoArticleWriter output)'),
});
var contentInputSchema = zod_1.z.discriminatedUnion('contentType', [
    markdownContentSchema,
    blocksContentSchema,
    sectionsContentSchema,
]);
var wordpressPublishContentArgsSchema = zod_1.z.discriminatedUnion('contentType', [
    zod_1.z.object({
        contentType: zod_1.z.literal('page'),
        title: zod_1.z.string().describe('The content title'),
        contentInput: contentInputSchema.describe('Content format: markdown (will convert), blocks (from getContent), or sections (structured data)'),
        status: zod_1.z
            .enum(['publish', 'draft', 'pending', 'private'])
            .default('publish')
            .describe('The content status'),
        slug: zod_1.z.string().nullish().describe('Optional URL-friendly slug'),
        excerpt: zod_1.z.string().nullish().describe('Optional brief excerpt or description'),
        featuredMediaId: zod_1.z
            .number()
            .nullish()
            .describe('Optional WordPress media ID for featured image'),
        commentStatus: zod_1.z
            .enum(['open', 'closed'])
            .nullish()
            .describe('Optional comment status (open or closed)'),
        pingStatus: zod_1.z
            .enum(['open', 'closed'])
            .nullish()
            .describe('Optional ping/trackback status (open or closed)'),
        parentId: zod_1.z.number().nullish().describe('Optional parent page ID for hierarchical pages'),
        menuOrder: zod_1.z.number().nullish().describe('Optional menu order for the page'),
    }),
    zod_1.z.object({
        contentType: zod_1.z.literal('post'),
        title: zod_1.z.string().describe('The content title'),
        contentInput: contentInputSchema.describe('Content format: markdown (will convert), blocks (from getContent), or sections (structured data)'),
        status: zod_1.z
            .enum(['publish', 'draft', 'pending', 'private'])
            .default('publish')
            .describe('The content status'),
        slug: zod_1.z.string().nullish().describe('Optional URL-friendly slug'),
        excerpt: zod_1.z.string().nullish().describe('Optional brief excerpt or description'),
        featuredMediaId: zod_1.z
            .number()
            .nullish()
            .describe('Optional WordPress media ID for featured image'),
        commentStatus: zod_1.z
            .enum(['open', 'closed'])
            .nullish()
            .describe('Optional comment status (open or closed)'),
        pingStatus: zod_1.z
            .enum(['open', 'closed'])
            .nullish()
            .describe('Optional ping/trackback status (open or closed)'),
        categories: zod_1.z
            .array(zod_1.z.number())
            .nullish()
            .describe('Array of category IDs to assign to the post'),
        tags: zod_1.z.array(zod_1.z.number()).nullish().describe('Array of tag IDs to assign to the post'),
    }),
]);
// Content update schemas
var contentUpdateSchema = zod_1.z.discriminatedUnion('updateType', [
    zod_1.z.object({
        updateType: zod_1.z.literal('blocks'),
        content: zod_1.z
            .string()
            .describe('Replace entire page content with raw WordPress Gutenberg blocks obtained from getContent - DO NOT generate blocks manually'),
    }),
    zod_1.z.object({
        updateType: zod_1.z.literal('sections'),
        sections: zod_1.z
            .array(zod_1.z.any())
            .min(1)
            .describe('Replace entire page content with structured sections that generate proper WordPress Gutenberg blocks (compatible with seoArticleWriter)'),
    }),
    zod_1.z.object({
        updateType: zod_1.z.literal('changes'),
        changes: zod_1.z
            .array(zod_1.z.any())
            .min(1)
            .describe('Apply incremental changes to existing content (compatible with seoArticleUpdater)'),
    }),
    zod_1.z.object({
        updateType: zod_1.z.literal('none'),
    }),
]);
var wordpressUpdateContentArgsSchema = zod_1.z.discriminatedUnion('contentType', [
    zod_1.z.object({
        contentType: zod_1.z.literal('page'),
        contentUrl: zod_1.z.string().describe('The URL or ID of the WordPress content to update'),
        title: zod_1.z.string().nullish().describe('Optional new title'),
        contentUpdate: contentUpdateSchema
            .nullish()
            .describe('How to update content: blocks (from getContent), sections (structured), changes (incremental), or none'),
        status: zod_1.z
            .enum(['publish', 'draft', 'pending', 'private'])
            .nullish()
            .describe('Optional new status'),
        slug: zod_1.z.string().nullish().describe('Optional new URL-friendly slug'),
        excerpt: zod_1.z.string().nullish().describe('Optional new brief excerpt or description'),
        featuredMediaId: zod_1.z
            .number()
            .nullish()
            .describe('Optional WordPress media ID for featured image'),
        commentStatus: zod_1.z
            .enum(['open', 'closed'])
            .nullish()
            .describe('Optional comment status (open or closed)'),
        pingStatus: zod_1.z
            .enum(['open', 'closed'])
            .nullish()
            .describe('Optional ping/trackback status (open or closed)'),
        parentId: zod_1.z.number().nullish().describe('Optional parent page ID for hierarchical pages'),
        menuOrder: zod_1.z.number().nullish().describe('Optional menu order for the page'),
    }),
    zod_1.z.object({
        contentType: zod_1.z.literal('post'),
        contentUrl: zod_1.z.string().describe('The URL or ID of the WordPress content to update'),
        title: zod_1.z.string().nullish().describe('Optional new title'),
        contentUpdate: contentUpdateSchema
            .nullish()
            .describe('How to update content: blocks (from getContent), sections (structured), changes (incremental), or none'),
        status: zod_1.z
            .enum(['publish', 'draft', 'pending', 'private'])
            .nullish()
            .describe('Optional new status'),
        slug: zod_1.z.string().nullish().describe('Optional new URL-friendly slug'),
        excerpt: zod_1.z.string().nullish().describe('Optional new brief excerpt or description'),
        featuredMediaId: zod_1.z
            .number()
            .nullish()
            .describe('Optional WordPress media ID for featured image'),
        commentStatus: zod_1.z
            .enum(['open', 'closed'])
            .nullish()
            .describe('Optional comment status (open or closed)'),
        pingStatus: zod_1.z
            .enum(['open', 'closed'])
            .nullish()
            .describe('Optional ping/trackback status (open or closed)'),
        categories: zod_1.z
            .array(zod_1.z.number())
            .nullish()
            .describe('Array of category IDs to assign (replaces existing)'),
        tags: zod_1.z.array(zod_1.z.number()).nullish().describe('Array of tag IDs to assign (replaces existing)'),
    }),
]);
var wordpressUploadMediaArgsSchema = zod_1.z.object({
    fileUrl: zod_1.z.string().url().describe('URL of the file to upload to WordPress media library'),
    fileName: zod_1.z
        .string()
        .nullish()
        .describe('Optional filename (will be extracted from URL if omitted)'),
    altText: zod_1.z.string().nullish().describe('Optional alt text for accessibility and SEO'),
    title: zod_1.z.string().nullish().describe('Optional media title'),
    caption: zod_1.z.string().nullish().describe('Optional media caption'),
    description: zod_1.z.string().nullish().describe('Optional media description'),
});
var wordpressGetMediaArgsSchema = zod_1.z.object({
    mediaId: zod_1.z.number().describe('The WordPress media ID'),
});
var wordpressUpdateMediaArgsSchema = zod_1.z.object({
    mediaId: zod_1.z.number().describe('The WordPress media ID'),
    altText: zod_1.z.string().nullish().describe('New alt text for accessibility and SEO'),
    title: zod_1.z.string().nullish().describe('New media title'),
    caption: zod_1.z.string().nullish().describe('New media caption'),
    description: zod_1.z.string().nullish().describe('New media description'),
});
var wordpressListCategoriesArgsSchema = zod_1.z.object({
    search: zod_1.z.string().nullish().describe('Search query to filter categories'),
    perPage: zod_1.z.number().min(1).max(100).default(100).describe('Number of categories per page'),
});
var wordpressListTagsArgsSchema = zod_1.z.object({
    search: zod_1.z.string().nullish().describe('Search query to filter tags'),
    perPage: zod_1.z.number().min(1).max(100).default(100).describe('Number of tags per page'),
});
// WordPress WebPublisher Args Schema
exports.wordpressWebPublisherArgsSchema = zod_1.z.object({
    action: exports.WordPressWebPublisherAction.describe('CRITICAL TOOL ROUTING: WordPress web publishing action to perform. Core actions - create (ONLY with SEO Article Writer node references): publish new WordPress pages/posts from seoArticleWriter output, update (ONLY with SEO Article Updater node references): modify existing WordPress content from seoArticleUpdater output, subagentTaskRunner: natural language WordPress tasks'),
    args: zod_1.z
        .union([
        // Core WebPublisher actions
        webPublisher_1.webPublisherArgsSchema.shape.args,
        // Extended WordPress actions
        wordpressGetContentArgsSchema,
        wordpressListContentArgsSchema,
        wordpressPublishContentArgsSchema,
        wordpressUpdateContentArgsSchema,
        wordpressUploadMediaArgsSchema,
        wordpressGetMediaArgsSchema,
        wordpressUpdateMediaArgsSchema,
        wordpressListCategoriesArgsSchema,
        wordpressListTagsArgsSchema,
    ])
        .describe('MANDATORY NODE TYPE RESTRICTIONS: Core actions require specific workflow node references - create actions MUST use seoArticleWriterNodeId, update actions MUST use seoArticleUpdaterNodeId. Extended actions use specific WordPress API parameters.'),
});
// Legacy result schemas - removed since we now use unified WebPublisherResult format
// All WordPress WebPublisher actions now use the unified WebPublisherResult format
var WordPressWebPublisherAllResultsSchema = webPublisher_1.webPublisherResultSchema;
// Dynamic result schemas keyed by action - all use unified format now
var wordpressWebPublisherResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action'],
    variants: [
        // Core WebPublisher actions
        { when: { action: 'create' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'update' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'subagentTaskRunner' }, schema: webPublisher_1.webPublisherResultSchema },
        // Extended WordPress actions - all use unified format
        { when: { action: 'getContent' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'listContent' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'publishContent' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'updateContent' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'uploadMedia' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'getMedia' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'updateMedia' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'listCategories' }, schema: webPublisher_1.webPublisherResultSchema },
        { when: { action: 'listTags' }, schema: webPublisher_1.webPublisherResultSchema },
    ],
    defaultSchema: webPublisher_1.webPublisherResultSchema,
});
exports.WORDPRESS_WEB_PUBLISHER_CATEGORY = common_1.ToolCategorySchema.enum.communication;
exports.WORDPRESS_WEB_PUBLISHER_COLOR = common_1.BRAND_COLORS.WORDPRESS;
exports.WORDPRESS_WEB_PUBLISHER_BG = (0, common_1.createBgColor)(exports.WORDPRESS_WEB_PUBLISHER_COLOR);
// WordPress WebPublisher tool metadata
exports.wordpressWebPublisher = (0, common_1.createToolMetadata)({
    id: 'wordpressWebPublisher',
    displayName: 'WordPress Web Publisher',
    displayDescription: 'CRITICAL TOOL ROUTING: WordPress web publisher with strict node type restrictions. Core actions - create (ONLY with SEO Article Writer node references): publish new WordPress pages/posts from seoArticleWriter output, update (ONLY with SEO Article Updater node references): modify existing WordPress content from seoArticleUpdater output, subagentTaskRunner: natural language WordPress tasks. Extended: full WordPress API operations for content, media, and taxonomy',
    category: exports.WORDPRESS_WEB_PUBLISHER_CATEGORY,
    color: exports.WORDPRESS_WEB_PUBLISHER_COLOR,
    backgroundColor: exports.WORDPRESS_WEB_PUBLISHER_BG,
    icon: 'wordpress',
    argsSchema: exports.wordpressWebPublisherArgsSchema,
}, wordpressWebPublisherResultSchemas);
